import { ComponentProps, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TopicTooltip } from '@/components/TopicTags/TopicTooltip';
import { TopicTag } from '@/features/analyze/components/Tag';

export type TopicTagWithTooltipProps = {
  name: string;
  description?: string | null;
  themes?: readonly string[] | null;
  isSocialIssue?: boolean | null;
} & Pick<ComponentProps<typeof TopicTag>, 'icon' | 'onClick'>;

export const TopicTagWithTooltip = ({
  name,
  description,
  themes,
  isSocialIssue,
  icon,
  onClick,
}: TopicTagWithTooltipProps) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  const openTooltip = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const margin = 12;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, margin),
      window.innerWidth - margin
    );
    setPosition({
      left,
      top: rect.top - 8,
    });
    setIsOpen(true);
  };

  const closeTooltip = () => setIsOpen(false);

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
      onFocus={openTooltip}
      onBlur={closeTooltip}
    >
      <TopicTag
        icon={icon}
        text={name}
        isSocialIssue={isSocialIssue ?? false}
        onClick={onClick}
        aria-describedby={tooltipId}
        tabIndex={0}
      />
      {isOpen &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[9999]"
            style={{
              left: position.left,
              top: position.top,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <TopicTooltip
              id={tooltipId}
              name={name}
              description={description}
              themes={themes}
              isSocialIssue={isSocialIssue ?? false}
            />
          </div>,
          document.body
        )}
    </div>
  );
};
